import React, { useState } from "react";
import { Tick } from "../../assets/icons";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { formatTime, getFirstLetters, toLocalISOString } from "../../utilities/commonUtilities";
import useCommentsFacade from "../../facade/comments/comments.Facade";
import WidgetLoader from "../common/widgetloader";
import { LABELS } from "../../constants/labels/chatLog.labels";

interface ChatProps {
  comments: any;
  setComments: any;
  handlePostComment?: any;
  handleEditComment?: any;
  handleDeleteComment?: any;
  showTextarea?: boolean;
  isEditing?: boolean;
  setIsEditing?: any;
  selectedId?: any;
  setSelectedId?: any;
}

const Chat: React.FC<ChatProps> = ({
  comments,
  setComments,
  isEditing,
  setIsEditing,
  selectedId,
  setSelectedId,
  handlePostComment,
  handleEditComment,
  handleDeleteComment,
  showTextarea,
}) => {
  const { user } = useAppSelector((state) => state?.auth);
  const { commentText, loading } = useAppSelector((state) => state?.comments);
  const { updateCommentText } = useCommentsFacade();
  const [editId, setEditId] = useState<any>(null);

  const handlePost = () => {
    if (commentText && commentText?.trim() === "") return;
    if (isEditing && editId) {
      const editComment = {
        comments: commentText || "",
        modifiedBy: user?.userID,
        createdBy: user?.userID,
        createdByName: user?.userFullName,
        id: editId,
      };
      setComments(
        (Array.isArray(comments) ? comments : []).map((c: any) =>
          c.id?.toString() === editId ? { ...c, ...editComment } : c
        )
      );
      setIsEditing(false);
      setEditId(null);
      handleEditComment(editId);
    } else {
      const newComment = {
        id: 0,
        comments: commentText || "",
        createdByName: user?.userFullName,
        createdBy: user?.userID,
        modifiedBy: user?.userID,
        CreatedOn: toLocalISOString(new Date()),
      };
      setComments([...(Array.isArray(comments) ? comments : []), newComment]);
      handlePostComment(newComment);
    }
    updateCommentText("");
    setSelectedId(null);
  };

  const handleEdit = (id: string) => {
    const comment = (Array.isArray(comments) ? comments : []).find((c: any) => c.id?.toString() === id);
    if (!comment) return;
    updateCommentText(comment.comments);
    setIsEditing(true);
    setEditId(id);
  };

  const handleDelete = (id: string) => {
    setSelectedId(null);
    handleDeleteComment(id);
  };

  return (
    <>
      <div
        className="flex flex-col w-full relative"
        style={{ height: "100vh" }}
        onClick={() => {
          setSelectedId(null);
        }}
      >
        <div className="flex-1 flex flex-col p-4">
          <div className="text-white w-full rounded flex flex-col">
            <div className="space-y-3">
              {loading ? (
                <div className="h-screen">
                  <WidgetLoader isLoading={loading} />
                </div>
              ) : comments?.length > 0 ? (
                comments?.map((comment: any) => (
                  <div
                    key={comment.id}
                    className={`flex ${comment.createdBy === user?.userID
                      ? "justify-end ml-[102px]"
                      : "justify-start mr-[102px]"
                      }`}
                  >
                    <div
                      className={`flex flex-col relative py-2 rounded-lg group`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(comment?.id);
                      }}
                    >
                      <div
                        className={`flex flex-col gap-[14px] p-3 text-neutral-25 rounded-[8px] p-[14px] border-0 ${comment.createdBy === user?.userID
                          ? "bg-darkcolor-700 hover:border-[1px] border-darkcolor-400"
                          : "bg-darkcolor-600"
                          } ${selectedId === comment.id &&
                            comment.createdBy === user?.userID
                            ? "border-[1px] border-primary-400"
                            : ""
                          }`}
                        key={comment.id}
                      >
                        <div className="flex items-center justify-start">
                          <span
                            className={`${comment.createdBy === user?.userID
                              ? ""
                              : "border w-[30px] h-[30px] rounded-[30px] border-darkcolor-400 bg-primary-400 text-sm flex items-center justify-center text-neutral-25"
                              }`}
                          >
                            {comment.createdBy === user?.userID
                              ? ""
                              : `${getFirstLetters(comment?.createdByName)}`}
                          </span>
                          <p
                            className={`text-[16px] font-medium text-neutral-25 ${comment.createdBy === user?.userID ? "" : "pl-2"
                              }`}
                          >
                            {comment.createdBy === user?.userID
                              ? "You"
                              : comment.createdByName}
                          </p>
                          <span className="text-[12px] font-normal text-neutral-200 pl-1">
                            · {formatTime(comment?.createdOn || "")}
                          </span>
                        </div>
                        <p className="flex flex-wrap text-[14px] font-normal text-white break-word whitespace-pre-wrap">
                          {comment.comments}
                        </p>
                      </div>
                      {comment.createdBy === user?.userID && (
                        <div
                          className={`absolute right-0 top-full pb-4"
                            rounded shadow-lg flex gap-3 text-sm z-10 ${selectedId === comment.id ? "block" : "hidden group-hover:flex"}`}
                        >
                          <button
                            className="text-error-500 hover:underline"
                            onClick={() => handleDelete(comment.id.toString())}
                          >
                            Delete
                          </button>
                          <button
                            className="text-primary-400 hover:underline"
                            onClick={() => handleEdit(comment.id.toString())}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : showTextarea ? (
                <div style={{height:"70vh"}} className="flex justify-center items-center text-gray-400 text-base">
                  {LABELS?.firstComment}
                </div>
              ) : (
                <div style={{height:"80vh"}} className="flex justify-center items-center text-gray-400 text-base">
                  {LABELS?.noComments}
                </div>
              )}
            </div>
          </div>
        </div>
        {showTextarea && (
          <div className="sticky bottom-0 bg-darkcolor-700 border-t-[1px] border-t-darkcolor-400 px-6 py-4 flex flex-col justify-end z-[10011]">
            <div className="flex justify-between items-center">
              <label className="text-[14px] font-medium text-secondary-75 pb-[8px]">
                {LABELS?.yourComment}
              </label>
              {isEditing && (
                <button
                  onClick={() => {
                    updateCommentText("");
                    setIsEditing(false);
                    setEditId(null);
                    setSelectedId(null);
                  }}
                  className="text-[14px] text-secondary-400 hover:underline mb-2 self-start"
                >
                  Discard
                </button>
              )}
            </div>
            <textarea
              rows={3}
              className="w-full border-darkcolor-400 border-[1px] py-[12px] px-[14px] mb-4 rounded-[8px] text-neutral-25 bg-darkcolor-800 p-[16px] focus:outline-none hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500 resize-none overflow-auto"
              placeholder={LABELS?.placeHolder}
              value={commentText}
              onChange={(e) => updateCommentText(e.target.value)}
              maxLength={200}
            />
            <button
              onClick={handlePost}
              className={`px-[14px] py-[10px] rounded text-white text-[14px] font-bold ${!commentText || commentText.trim() === ""
                ? "bg-darkcolor-400 border-0 border-darkcolor-400 rounded-lg opacity-50 cursor-not-allowed"
                : "bg-primary-500 border-0 border-primary-500 rounded-lg hover:bg-primary-500 opacity-90"
                }`}
              disabled={!commentText || commentText.trim() === ""}
            >
              {isEditing ? (
                `${LABELS?.updateComment}`
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Tick size={20} color="#F7F9F9" />
                  {LABELS?.postComment}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Chat;
